import { useCallback, useMemo } from 'react';

import {
  useErpCareerCourseSkillsLazyQuery,
  useErpCareerCourseSkillSummaryQuery,
  useSaveCourseSkillsMutation,
} from 'core/graphql';

import {
  ICourseSkillRow,
  ICourseSkillSummary,
  ISkillMutationResult,
} from './careerModel';

const EMPTY_SUMMARY: ICourseSkillSummary[] = [];
const EMPTY_ROWS: ICourseSkillRow[] = [];

/**
 * ТЗ 0039 — «какие навыки даёт курс».
 *
 * Курсами ERP не управляет вовсе (страница курсов — read-only таблица из трёх
 * колонок, мутаций курса фронт не зовёт), поэтому редактор живёт здесь, рядом
 * со справочником навыков: тут работают те же люди.
 */
export const useCourseSkillSummary = () => {
  const { data, loading, refetch } = useErpCareerCourseSkillSummaryQuery({
    fetchPolicy: 'cache-and-network',
  });
  return {
    courses: data?.careerCourseSkillSummary ?? EMPTY_SUMMARY,
    loading,
    refetch,
  };
};

export const useCourseSkills = () => {
  const [load, { data, loading }] = useErpCareerCourseSkillsLazyQuery({
    fetchPolicy: 'network-only',
  });

  const rows: ICourseSkillRow[] =
    data?.careerCourseSkills?.map((row) => ({
      id: row.id,
      courseId: row.courseId,
      courseName: row.courseName,
      skillId: row.skillId,
      skillName: row.skillName,
      skillCategory: row.skillCategory,
      level: row.level,
      unlockLessonNumber: row.unlockLessonNumber ?? null,
      isActive: row.isActive,
    })) ?? EMPTY_ROWS;

  // Сервер отдаёт и деактивированные связи (они хранят историю начислений);
  // редактору нужны только живые.
  // 🔴 `useMemo` обязателен. `rows.filter(...)` создаёт НОВЫЙ массив на каждый
  // рендер, а `CoursesPanel` делает `useEffect(() => setDraft(rows.map(...)),
  // [rows])`. Новая ссылка → зависимость «изменилась» → setState → рендер →
  // снова новая ссылка: подвкладка «Навыки курсов» уходила в бесконечный цикл
  // с «Maximum update depth exceeded» в консоли (воспроизведено в браузере).
  // Тот же класс дефекта, от которого бережёт `useStableRefetch`.
  const activeRows = useMemo(
    () => rows.filter((row) => row.isActive),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  return {
    rows: activeRows,
    loading,
    loadForCourse: useCallback(
      (courseId: string) => load({ variables: { courseId } }),
      [load],
    ),
    /**
     * Все связи разом — для обратного взгляда «в каких курсах этот навык».
     * Владелец попросил именно его: «хочу у навыка выбрать курсы».
     */
    loadAll: useCallback(() => load({ variables: {} }), [load]),
  };
};

export const useSetCourseSkills = () => {
  const [save, { loading }] = useSaveCourseSkillsMutation();

  const setSkills = useCallback(
    async (
      courseId: string,
      rows: {
        skillId: string;
        level: number;
        unlockLessonNumber?: number | null;
      }[],
    ): Promise<ISkillMutationResult> => {
      const { data } = await save({
        variables: {
          courseId,
          rows: rows.map((row, index) => ({
            skillId: row.skillId,
            level: row.level,
            // Сервер понимает пусто и 0 одинаково — «по завершении курса».
            // Шлём null явно, чтобы снятая привязка ЗАТИРАЛА прежнюю, а не
            // молча оставляла старый номер (`setCourseSkills` пишет набор
            // целиком, поэтому пропущенное поле — это не «не менять»).
            unlockLessonNumber: row.unlockLessonNumber ?? null,
            order: index,
          })),
        },
      });
      const payload = data?.setCourseSkills;
      return {
        success: Boolean(payload?.success),
        errors: (payload?.customErrors ?? []).map((item) => ({
          field: item?.field ?? '',
          message: item?.message ?? '',
        })),
      };
    },
    [save],
  );

  return { setSkills, saving: loading };
};
